import React, { FunctionComponent, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonRefresher,
  IonRefresherContent,
  IonGrid,
  IonRow,
  IonCol,
  IonLabel,
  IonInfiniteScroll,
  IonInfiniteScrollContent
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { SiteItem } from './site-item.component';
import { RouteBuilder } from '../../router/constantes/routes.const';
import { EditIonCardSiteList } from './edit-ion-card-site-list.component';
import { useEditRefresher } from '../../shared/hooks/use-edit-refresher.hook';
import { LoadingSiteListComponent } from './loading-site-list.component';
import { EmptySiteListComponent } from './empty-site-list.component';
import { useSiteList } from '../hooks/use-site-list.hook';
import { useSitesCache } from '../../shared/hooks/use-sites-cache.hook';
import { FilterComponent } from '../../shared/components/filter.component';
import { MigrationBannerComponent } from './migration-banner.component';
import { searchOutline, closeOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useOrganizationCode } from '../../router/hooks/use-organization-code.hook';
import { useLocale } from '../../router/hooks/use-locale.hook';
import { addDebugLog } from '../../shared/hooks/use-debug-logs.hook';
import './site-list.component.scss';

export const SiteList: FunctionComponent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const organizationCode = useOrganizationCode();
  const locale = useLocale();
  const contentRef = React.useRef<HTMLIonContentElement>(null);

  const {
    fetchSiteList,
    loadMoreSites,
    handleSearch,
    clearSearch,
    siteList,
    siteListFiltered,
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    handleAnnotationFilterChange,
    hasMore,
    isLoading,
    loadCounter
  } = useSiteList();

  // Ne pas appeler fetchSiteList au montage si l'organisation n'est pas encore disponible
  // Le useEffect dans use-site-list.hook.ts se chargera de charger les sites quand l'organisation sera disponible
  React.useEffect(() => {
    addDebugLog('app', `[SiteList] Component mounted, organization: ${organizationCode || 'not available yet'}`);
    // Ne pas appeler fetchSiteList ici, attendre que l'organisation soit disponible
    // Le hook useSiteList gère le chargement via le useEffect qui écoute currentOrganization
  }, [organizationCode]);

  // Alimenter le cache partagé avec les sites récupérés
  useSitesCache(siteList);

  // Wrapper pour fetchSiteList qui ne prend pas de paramètres (pour useEditRefresher)
  const refreshSiteList = React.useCallback(async () => {
    await fetchSiteList();
  }, [fetchSiteList]);

  const { ionRefresherRef, refreshCallback } = useEditRefresher(refreshSiteList);

  // Charger automatiquement plus de sites si le contenu ne remplit pas l'écran (UNIQUEMENT sans recherche)
  React.useEffect(() => {
    const checkAndLoadMore = async () => {
      // Ne charger automatiquement QUE s'il n'y a PAS de recherche active
      if (activeSearchTerm !== '') return;
      if (!siteList || siteList.length === 0 || isLoading || !hasMore) return;
      
      const content = contentRef.current;
      if (content) {
        const scrollElement = await content.getScrollElement();
        const hasScroll = scrollElement.scrollHeight > scrollElement.clientHeight;
        
        // Si pas de scroll et qu'il y a plus de sites, charger automatiquement
        if (!hasScroll && hasMore) {
          loadMoreSites();
        }
      }
    };
    
    // Petit délai pour laisser le DOM se mettre à jour
    const timer = setTimeout(checkAndLoadMore, 100);
    return () => clearTimeout(timer);
  }, [siteList, hasMore, isLoading, loadMoreSites, activeSearchTerm]);

  const handleSearchChange = (e: CustomEvent) => {
    const value = e.detail.value!;
    setSearchTerm(value);
    
    // Si le champ est vidé manuellement et qu'il y avait une recherche active, réinitialiser
    if (value === '' && activeSearchTerm !== '') {
      clearSearch();
    }
  };

  const handleSearchSubmit = () => {
    handleSearch();
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const [isFocused, setIsFocused] = useState(false);

  const navigateToAnnotations = () => {
    history.push(RouteBuilder.annotations(locale, organizationCode));
  };

  const handleInfiniteScroll = async (ev: any) => {
    await loadMoreSites();
    ev?.target?.complete();
  };

  return (
    <>
    <IonContent ref={contentRef}>
      <IonRefresher ref={ ionRefresherRef } slot="fixed" onIonRefresh={ refreshCallback }>
        <IonRefresherContent/>
      </IonRefresher>
      
      <div className="fixed-top-container">
        <MigrationBannerComponent />
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonItem
                lines="none"
                style={{
                  width: 'calc(100% - 10px)',
                  maxWidth: '100%',
                  background: '#F7F8FB',
                  border: '1px solid #ccc',
                  borderRadius: '100px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  alignItems: 'center',
                  height: '40px',
                  display: 'flex',
                  marginLeft: '5px',
                  marginRight: '5px',
                }}
              >
                <IonIcon
                  icon={searchOutline}
                  slot="start"
                  style={{
                    fontSize: '16px',
                    color: !searchTerm ? '#888' : '#fff',
                    backgroundColor: !searchTerm
                      ? 'transparent'
                      : isFocused
                        ? '#165BE6'
                        : '#4B4D55',
                    borderRadius: '50%',
                    padding: '4px',
                    height: '24px',
                    width: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.3s',
                    marginRight: '6px',
                    verticalAlign: 'middle',
                  }}
                />

                <IonInput
                  placeholder={t('sites.searchPlaceholder')}
                  value={searchTerm}
                  onIonInput={handleSearchChange}
                  onKeyPress={handleKeyPress}
                  onIonFocus={() => setIsFocused(true)}
                  onIonBlur={() => setIsFocused(false)}
                  style={{
                    font:'open-sans, sans-serif',
                    weight: '400',
                    background: 'transparent',
                    flex: 1,
                    fontSize: '12px',
                    lineHeight: '100%',
                    verticalAlign: 'middle',
                    height:'100%',
                    alignItems: 'center',
                  }}
                  clearInput={false}
                  role="search"
                  enterkeyhint="search"
                />

                {searchTerm && (
                  <>
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={clearSearch}
                      style={{
                        padding: '2px',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        verticalAlign: 'middle',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      className="no-hover-bg"
                    >
                      <IonIcon
                        icon={closeOutline}
                        slot="icon-only"
                        style={{
                          fontSize: '14px',
                          color: '#888',
                          backgroundColor: 'transparent',
                        }}
                      />
                    </IonButton>
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={handleSearchSubmit}
                      style={{
                        padding: '2px',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        verticalAlign: 'middle',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      className="no-hover-bg"
                    >
                      <IonIcon
                        icon={searchOutline}
                        slot="icon-only"
                        style={{
                          fontSize: '16px',
                          color: isFocused ? '#165BE6' : '#4B4D55',
                          backgroundColor: 'transparent',
                        }}
                      />
                    </IonButton>
                  </>
                )}
              </IonItem>
            </IonCol>
          </IonRow>
        </IonGrid>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 16px'
        }}>
          <IonLabel style={{ 
            fontSize: '14px',
            margin: 0,
            fontFamily: 'Open Sans, sans-serif',
            fontWeight: 600,
            lineHeight: '1.5',
            letterSpacing: '0.5px',
            color: '#191A20'
          }}>
            {t('sites.listHeader')}
          </IonLabel>
          <FilterComponent
            title={ '' }
            buttonTitle={ t('sites.filter.button') }
            options={ [{ value: 'ALL', key: 0, label: t('sites.filter.options.all') }, {
              value: 'ANNOTATED',
              key: 1,
              label: t('sites.filter.options.annotated')
            }] }
            onSelectionChanged={ (value: string) => {
              if (value === 'ALL' || value === 'ANNOTATED') handleAnnotationFilterChange(value);
            } }
            buttonStyle={{
              color: '#4B4D56',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500',
              fontSize: '12px',
              lineHeight: '20px',
              fontFamily: 'open-sans, sans-serif',
              letterSpacing: '0.5px',
              textTransform: 'none'
            }}
          />
        </div>
      </div>

      <div className="content-with-fixed-top content-with-bottom-button">
        { siteList == null ? (
          <LoadingSiteListComponent message={ t('sites.loadingMessage') }/>
        ) : siteListFiltered.length === 0 ? (
          <EmptySiteListComponent
            refreshSiteList={ fetchSiteList }
            message={ t('sites.emptyMessage') }
            retryLabel={ t('sites.retryButton') }
          />
        ) : (
          <EditIonCardSiteList
            list={ siteListFiltered }
            title={ '' }
            itemChild={ (site) => <SiteItem site={ site }/> }
            getRouterLink={ (siteId) => RouteBuilder.site(siteId, locale, organizationCode) }
          />
        )}
      </div>

      <IonInfiniteScroll
        key={`infinite-scroll-${loadCounter}`}
        onIonInfinite={handleInfiniteScroll}
        threshold="500px"
        disabled={!hasMore || isLoading}
      >
        <IonInfiniteScrollContent
          loadingSpinner="bubbles"
          loadingText={t('sites.loadingMore')}
        />
      </IonInfiniteScroll>
    </IonContent>
    <IonButton
    expand="block"
    onClick={navigateToAnnotations}
    style={{
      color: '#FFFFFF',
      backgroundColor: '#FF2953',
      height: '52px',
      margin: '0',
      '--padding-start': '12px',
      '--padding-end': '12px',
      '--background': '#FF2953',
      textTransform: 'none',
      position: 'fixed',
      bottom: 'env(safe-area-inset-bottom, 0)',
      left: 0,
      right: 0,
      width: '100%',
      background: '#F7F8FB',
      padding: '8px',
      boxShadow: '1px 1px 3px 0px var(--elevation-xs, rgba(0, 0, 0, 0.20))',
      zIndex: 1000,
      transform: 'translateZ(0)',
      WebkitTransform: 'translateZ(0)',
      WebkitBackfaceVisibility: 'hidden',
      WebkitPerspective: '1000'
    }}
  >
    {t('sites.annotationsListButton')}
  </IonButton>
  </>
  );
};
